/**
 * Table recording controls
 * - RecordingBadge: red REC pill everyone at the table sees while a recording runs
 * - RecordingButton / RecordingMenuItem: opens the recording panel (desktop / phone menu)
 * - RecordingPanel: price, start/stop, live countdown
 * - RecordingConsentGate: asks other seated players to agree; a decline turns
 *   their camera and mic off for the rest of the recording
 *
 * Recording is a paid add-on: regular players pay a few PSS Coins per session.
 * Partners, and anyone at a table opened with a partner's bypass code, record free.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Circle, Square, Video } from 'lucide-react';
import gameApiService from '../../services/gameApiService';

interface TableRecordingInfo {
  id: string;
  ownerUid: string;
  ownerName?: string;
  startedAt: number;
  maxEndsAt: number;
  showCards?: boolean;
}

export interface RecordingRules {
  enabled: boolean;
  partner: boolean;
  free: boolean;
  freeReason: 'partner' | 'partner_table' | null;
  pricePssCoins: number;
  balance: number;
  canAfford: boolean;
  maxSessionMinutes: number;
  retentionDays: number;
  active: any;
}

const recOf = (table: any): TableRecordingInfo | null => (table && table.recording && table.recording.id ? table.recording : null);

export const priceLabel = (r: Pick<RecordingRules, 'free' | 'pricePssCoins'> | null | undefined) =>
  !r ? '' : r.free ? 'Free' : `${r.pricePssCoins} PSS Coins`;

const fmt = (sec: number) => {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};
const mins = (sec: number) => `${Math.floor(Math.max(0, sec) / 60)} min`;

function useNow(active: boolean) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

// ---------------------------------------------------------------------------
export const RecordingBadge: React.FC<{ table: any }> = ({ table }) => {
  const rec = recOf(table);
  const now = useNow(!!rec);
  if (!rec) return null;
  const elapsed = (now - rec.startedAt) / 1000;
  return (
    <span
      title={`${rec.ownerName || 'A player'} is recording this table`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999,
        background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.6)', color: '#fca5a5',
        fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: 999, background: '#ef4444', boxShadow: '0 0 8px #ef4444', animation: 'pssRecPulse 1.2s infinite' }} />
      REC {fmt(elapsed)}
      <style>{`@keyframes pssRecPulse{0%,100%{opacity:1}50%{opacity:.35}}`}</style>
    </span>
  );
};

// ---------------------------------------------------------------------------
/** Owner-only switch next to the REC badge: are my cards in the recording? */
export const RecordingCardsToggle: React.FC<{ table: any; currentUserId: string; asMenuItem?: boolean }> = ({ table, currentUserId, asMenuItem }) => {
  const rec = recOf(table);
  const [busy, setBusy] = useState(false);
  if (!rec || rec.ownerUid !== currentUserId) return null;
  const on = rec.showCards !== false;
  const flip = async () => {
    setBusy(true);
    try { await gameApiService.recordingsShowCards(rec.id, !on); } catch { /* badge stays until the server confirms */ }
    finally { setBusy(false); }
  };
  if (asMenuItem) {
    return (
      <button className="pss-mc-item" onClick={flip} disabled={busy}>
        {on ? 'Recording shows my cards: ON (tap to hide)' : 'Recording shows my cards: OFF (tap to show)'}
      </button>
    );
  }
  return (
    <button
      onClick={flip}
      disabled={busy}
      title={on ? 'Your cards are in the recording. Tap to hide them.' : 'Your cards are hidden in the recording. Tap to show them.'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8, cursor: 'pointer',
        background: on ? 'rgba(34,197,94,.15)' : 'rgba(255,255,255,.08)', border: `2px solid ${on ? '#22c55e' : '#94a3b8'}`,
        color: on ? '#86efac' : '#cbd5e1', fontSize: 13, fontWeight: 700,
      }}
    >
      🂠 Cards {on ? 'ON' : 'OFF'}
    </button>
  );
};

// ---------------------------------------------------------------------------
interface PanelProps {
  tableId: string;
  table: any;
  currentUserId: string;
  onClose: () => void;
}

export const RecordingPanel: React.FC<PanelProps> = ({ tableId, table, currentUserId, onClose }) => {
  const navigate = useNavigate();
  const [rules, setRules] = useState<RecordingRules | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCards, setShowCards] = useState(true);
  const rec = recOf(table);
  const mine = !!rec && rec.ownerUid === currentUserId;
  const now = useNow(!!rec);

  const load = useCallback(async () => {
    try {
      const r = await gameApiService.recordingsRules(tableId);
      setRules(r);
      if (r?.active && typeof r.active.showCards === 'boolean') setShowCards(r.active.showCards);
    } catch (e: any) { setError(e.message || 'Could not load recording info'); }
  }, [tableId]);
  useEffect(() => { load(); }, [load]);

  const start = async (withCards: boolean) => {
    setShowCards(withCards);
    setBusy(true); setError(null);
    try { await gameApiService.recordingsStart(tableId, withCards); await load(); }
    catch (e: any) { setError(e.message || 'Could not start recording'); }
    finally { setBusy(false); }
  };
  const stop = async () => {
    if (!rec) return;
    setBusy(true); setError(null);
    try { await gameApiService.recordingsStop(rec.id); await load(); }
    catch (e: any) { setError(e.message || 'Could not stop recording'); }
    finally { setBusy(false); }
  };

  const elapsed = rec ? (now - rec.startedAt) / 1000 : 0;
  const left = rec ? (rec.maxEndsAt - now) / 1000 : 0;
  const canStart = !rec && !!rules?.enabled && !!rules?.canAfford;

  const box: React.CSSProperties = {
    background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)', borderRadius: 16, padding: 20, width: '92%', maxWidth: 380,
    border: '1px solid #334155', color: '#e2e8f0', boxShadow: '0 20px 60px rgba(0,0,0,.6)',
  };
  const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
    width: '100%', padding: '12px 14px', borderRadius: 10, border: 'none', background: bg, color, fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 10,
  });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
      <div onClick={(e) => e.stopPropagation()} style={box}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Video size={18} color="#f87171" />
          <strong style={{ fontSize: 17 }}>Record this table</strong>
        </div>

        {!rules && !error && <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading...</div>}
        {rules && rules.enabled === false && (
          <div style={{ color: '#fbbf24', fontSize: 13 }}>Recording isn't switched on yet. Check back soon.</div>
        )}

        {rules && rules.enabled !== false && (
          <>
            <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.5 }}>
              {rules.free ? (
                <div>
                  <span style={{ color: '#4ade80', fontWeight: 700 }}>Free</span>
                  {rules.freeReason === 'partner' ? ' · partner account' : ' · this table was opened with a partner code'}
                </div>
              ) : (
                <div>
                  <strong style={{ color: '#e2e8f0' }}>{rules.pricePssCoins} PSS Coins</strong> per recording · you have {rules.balance.toFixed(2)}
                </div>
              )}
              <div>Up to {rules.maxSessionMinutes} minutes, then it stops itself. Files are kept for {rules.retentionDays} days.</div>
              <div>Voice and video without recording stay free.</div>
            </div>

            {rec && mine && (
              <div style={{ marginTop: 14, padding: 14, borderRadius: 12, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.5)', textAlign: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: '#fca5a5', fontVariantNumeric: 'tabular-nums' }}>{fmt(elapsed)}</div>
                <div style={{ fontSize: 12, color: left < 300 ? '#fbbf24' : '#94a3b8' }}>
                  {left < 300 ? `Stops in ${fmt(left)}` : `Stops automatically in ${mins(left)}`}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Leaving the table also stops it.</div>
              </div>
            )}
            {rec && !mine && (
              <div style={{ marginTop: 12, fontSize: 13, color: '#fbbf24' }}>{rec.ownerName || 'Another player'} is already recording this table.</div>
            )}

            {rec && mine ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, fontSize: 13 }}>
                  <span>My cards in the recording</span>
                  <RecordingCardsToggle table={table} currentUserId={currentUserId} />
                </div>
                <button style={btn('#ef4444')} disabled={busy} onClick={stop}>
                  <Square size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Stop recording
                </button>
              </>
            ) : (
              <>
                <button style={btn(canStart ? '#ef4444' : '#475569', canStart ? '#fff' : '#94a3b8')} disabled={busy || !canStart} onClick={() => start(true)}>
                  <Circle size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                  Record with my cards showing{rules.free ? ' · Free' : ` · ${rules.pricePssCoins} PSS Coins`}
                </button>
                <button style={btn(canStart ? 'rgba(239,68,68,.18)' : 'rgba(255,255,255,.06)', canStart ? '#fca5a5' : '#94a3b8')} disabled={busy || !canStart} onClick={() => start(false)}>
                  <Circle size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                  Record with my cards hidden
                </button>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Only your own cards are ever shown. Other players' cards stay hidden until showdown. You can switch while recording.</div>
              </>
            )}
            {!rules.free && !rules.canAfford && !rec && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#fbbf24' }}>Not enough PSS Coins for a recording.</div>
            )}
            <button style={btn('rgba(255,255,255,.06)', '#cbd5e1')} onClick={() => { onClose(); navigate('/recordings'); }}>My recordings</button>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 10, lineHeight: 1.4 }}>
              Everyone at the table sees a REC badge. Other players are asked to agree before their camera or mic is included. The recording shows the table as a spectator sees it.
            </div>
          </>
        )}
        {error && <div style={{ marginTop: 10, color: '#f87171', fontSize: 13 }}>{error}</div>}
        <button style={btn('transparent', '#94a3b8')} onClick={onClose}>Close</button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
interface ButtonProps { tableId: string; table: any; currentUserId: string; }

export const RecordingButton: React.FC<ButtonProps> = ({ tableId, table, currentUserId }) => {
  const [open, setOpen] = useState(false);
  const rec = recOf(table);
  const mine = !!rec && rec.ownerUid === currentUserId;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title={mine ? 'Recording - open controls' : 'Record this table'}
        style={{
          background: mine ? 'rgba(239,68,68,.2)' : 'rgba(255,255,255,.1)', border: `2px solid ${mine ? '#ef4444' : '#94a3b8'}`, borderRadius: 8,
          padding: '8px 12px', cursor: 'pointer', color: mine ? '#fca5a5' : '#cbd5e1', fontSize: 14, fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: 6,
        }}
      >
        <Circle size={12} fill={mine ? '#ef4444' : 'none'} color={mine ? '#ef4444' : '#cbd5e1'} />
        {mine ? 'Recording' : 'Record'}
      </button>
      {open && <RecordingPanel tableId={tableId} table={table} currentUserId={currentUserId} onClose={() => setOpen(false)} />}
    </>
  );
};

export const RecordingMenuItem: React.FC<ButtonProps & { onClose: () => void }> = ({ tableId, table, currentUserId, onClose }) => {
  const [open, setOpen] = useState(false);
  const rec = recOf(table);
  const mine = !!rec && rec.ownerUid === currentUserId;
  return (
    <>
      <button className="pss-mc-item" onClick={() => { onClose(); setOpen(true); }}>
        {mine ? 'Recording... (open controls)' : 'Record this table'}
      </button>
      {open && <RecordingPanel tableId={tableId} table={table} currentUserId={currentUserId} onClose={() => setOpen(false)} />}
    </>
  );
};

// ---------------------------------------------------------------------------
interface ConsentProps {
  table: any;
  currentUserId: string;
  seated: boolean;
  onBlockedChange: (blocked: boolean) => void;
}

export const RecordingConsentGate: React.FC<ConsentProps> = ({ table, currentUserId, seated, onBlockedChange }) => {
  const rec = recOf(table);
  const recId = rec?.id || null;
  const isOwner = !!rec && rec.ownerUid === currentUserId;
  const storageKey = recId ? `pss-rec-consent-${recId}` : null;
  const stored = useMemo(() => {
    if (!storageKey) return null;
    try { return sessionStorage.getItem(storageKey); } catch { return null; }
  }, [storageKey]);
  const [answer, setAnswer] = useState<string | null>(stored);
  useEffect(() => { setAnswer(stored); }, [stored]);

  // Blocked = an active recording by someone else that this player declined
  useEffect(() => {
    onBlockedChange(!!recId && !isOwner && answer === 'declined');
  }, [recId, isOwner, answer, onBlockedChange]);

  if (!rec || isOwner || !seated || !currentUserId || answer) return null;

  const respond = async (accept: boolean) => {
    const v = accept ? 'accepted' : 'declined';
    setAnswer(v);
    try { if (storageKey) sessionStorage.setItem(storageKey, v); } catch { /* noop */ }
    gameApiService.recordingsConsent(rec.id, accept).catch(() => {});
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}>
      <div style={{ background: 'linear-gradient(180deg,#1e293b,#0f172a)', border: '1px solid #ef4444', borderRadius: 16, padding: 22, width: '92%', maxWidth: 400, color: '#e2e8f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ width: 10, height: 10, borderRadius: 999, background: '#ef4444' }} />
          <strong style={{ fontSize: 17 }}>This table is being recorded</strong>
        </div>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: '#cbd5e1' }}>
          <strong>{rec.ownerName || 'A player'}</strong> started a recording. Your video, voice, username and chat may appear in it, and they can download and share it.
        </p>
        <p style={{ fontSize: 13, lineHeight: 1.5, color: '#94a3b8' }}>
          You can keep playing either way. If you don't agree, your camera and mic stay off until the recording ends.
        </p>
        <button onClick={() => respond(true)} style={{ width: '100%', padding: 12, borderRadius: 10, border: 'none', background: '#22c55e', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 8 }}>
          I agree
        </button>
        <button onClick={() => respond(false)} style={{ width: '100%', padding: 12, borderRadius: 10, border: '1px solid #475569', background: 'transparent', color: '#cbd5e1', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 8 }}>
          Play with camera and mic off
        </button>
      </div>
    </div>
  );
};
